import React, { useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Select, Tooltip, Button, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFilters } from "../../../redux/reports/reportsSlice";
// CUSTOM HOOKS
import useGetAllLiftsLight from "../../../customHooks/GET_requests/useGetAllLiftsLight";
import useMonitoringSelectedBankIds from "../../../customHooks/useMonitoringSelectedBankIds";
// FLATBUFFERS
import formatUuidT from "../../../flatbuffers/display/uuid";
// INTERFACES
import { RootState } from "../../../app/store";
import { LiftLightInterface } from "../../../interfaces/Lift.interface";
import { ReportSliceFiltersInterface } from "../../../interfaces/Reports.interface";
// SCOPE
import { useScopePredicate } from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

function ReportsFilterGetLifts() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option, OptGroup } = Select;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters, blockFilters } = useSelector((state: RootState) => state.report);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { liftsLight, isLoadingGetAllLiftsLight } = useGetAllLiftsLight();
  const monitoringBankIds = useMonitoringSelectedBankIds();
  const liftScopeAllows = useScopePredicate(PermissionAction.VIEW);

  // ************************************************ */
  // FILTERED DATA ********************************** */
  const filteredLifts = useMemo(() => {
    // Lifts inherit their org via their parent bank — when the user has
    // narrowed the monitoring dashboard to specific banks, the report's lift
    // picker should only show lifts inside those banks.
    let lifts = liftsLight;
    if (monitoringBankIds) {
      lifts = lifts.filter((lift: LiftLightInterface) =>
        monitoringBankIds.has(formatUuidT(lift.bank?.id)),
      );
    }
    // Apply per-lift VIEW scope: empty grant rows ⇒ allow-all.
    return lifts.filter((lift: LiftLightInterface) =>
      liftScopeAllows({
        bankId: formatUuidT(lift.bank?.id),
        liftId: formatUuidT(lift.id),
      }),
    );
  }, [liftsLight, monitoringBankIds, liftScopeAllows]);

  // ************************************************ */
  // GROUPED DATA *********************************** */
  const bankGroups = useMemo(() => {
    const bankMap = new Map<string, { id: string; name: string }>();
    filteredLifts.forEach((lift: LiftLightInterface) => {
      if (lift.bank) {
        const bIdStr = formatUuidT(lift.bank.id);
        if (bIdStr && !bankMap.has(bIdStr)) {
          bankMap.set(bIdStr, { id: bIdStr, name: lift.bank.name });
        }
      }
    });
    return Array.from(bankMap.values());
  }, [filteredLifts]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeLift = (newValue: string) => {
    const copy: ReportSliceFiltersInterface = structuredClone(filters);
    const lift = liftsLight.find((l: LiftLightInterface) => formatUuidT(l.id) === newValue);
    if (lift) {
      copy.liftId = formatUuidT(lift.id);
      copy.liftName = lift.name;
      if (lift.bank) {
        copy.bankId = formatUuidT(lift.bank.id);
        copy.bankName = lift.bank.name;
      }
    }
    dispatch(updateFilters(copy));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (filteredLifts.length === 0) return;
    const currentInScope = filteredLifts.some(
      (lift: LiftLightInterface) => formatUuidT(lift.id) === filters.liftId,
    );
    if (filters.liftId !== "" && currentInScope) return;
    onChangeLift(formatUuidT(filteredLifts[0].id));
  }, [filteredLifts]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__width100" style={{ display: "flex", alignItems: "center" }}>
      <Button size="small" disabled style={{ minWidth: "fit-content" }}>
        {t("report.selectLift")}
      </Button>
      <Tooltip title={filteredLifts.length === 0 ? t("report.noLifts") : ""}>
        <Select
          className="generalStyles__width100"
          disabled={filteredLifts.length === 0 || blockFilters}
          showSearch
          size="small"
          optionFilterProp="children"
          placeholder={t("report.selectLift")}
          loading={isLoadingGetAllLiftsLight}
          value={filters.liftId === "" ? null : filters.liftId}
          onChange={onChangeLift}
        >
          {bankGroups.map((group) => (
            <OptGroup key={group.id} label={`BANK / ${group.name}`}>
              {filteredLifts
                .filter((lift: LiftLightInterface) => formatUuidT(lift.bank?.id) === group.id)
                .map((lift: LiftLightInterface) => (
                  <Option key={formatUuidT(lift.id)} value={formatUuidT(lift.id)}>
                    <Tooltip
                      overlayInnerStyle={{ minWidth: "380px" }}
                      placement="right"
                      title={
                        <div>
                          <div style={{ marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>
                            {lift.name}
                          </div>
                          <div
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.15)",
                              marginBottom: "6px",
                              paddingBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                <b className="generalStyles__info">ID:</b>{" "}
                                <span style={{ fontSize: "11px", opacity: 0.85 }}>
                                  {formatUuidT(lift.id)}
                                </span>
                              </span>
                              <CopyToClipboard
                                text={formatUuidT(lift.id)}
                                onCopy={() => message.success("Lift ID copied")}
                              >
                                <FontAwesomeIcon
                                  icon={faCopy}
                                  style={{
                                    cursor: "pointer",
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                    opacity: 0.7,
                                  }}
                                />
                              </CopyToClipboard>
                            </div>
                          </div>
                          <div style={{ marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>
                            Bank
                          </div>
                          <div>
                            <b className="generalStyles__info">{lift.bank?.name}</b>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              <b className="generalStyles__info">ID:</b>{" "}
                              <span style={{ fontSize: "11px", opacity: 0.85 }}>
                                {formatUuidT(lift.bank?.id)}
                              </span>
                            </span>
                            <CopyToClipboard
                              text={formatUuidT(lift.bank?.id)}
                              onCopy={() => message.success("Bank ID copied")}
                            >
                              <FontAwesomeIcon
                                icon={faCopy}
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "8px",
                                  fontSize: "11px",
                                  opacity: 0.7,
                                }}
                              />
                            </CopyToClipboard>
                          </div>
                        </div>
                      }
                    >
                      {lift.name}
                    </Tooltip>
                  </Option>
                ))}
            </OptGroup>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
}

export default ReportsFilterGetLifts;
